import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import AssignmentLate from '@mui/icons-material/esm/AssignmentLate';
import CheckCircle from '@mui/icons-material/esm/CheckCircle';
import Delete from '@mui/icons-material/esm/Delete';
import Edit from '@mui/icons-material/esm/Edit';
import FactCheck from '@mui/icons-material/esm/FactCheck';
import PlaylistAddCheck from '@mui/icons-material/esm/PlaylistAddCheck';
import Refresh from '@mui/icons-material/esm/Refresh';
import ReportProblem from '@mui/icons-material/esm/ReportProblem';

import { KPICard, DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import inspectionsService, {
  ChecklistItemStatus,
  InspectionPayload,
  InspectionRow,
  InspectionStatus,
  InspectionType,
  NcrPayload,
  NcrRow,
  NcrSeverity,
  NcrStatus,
} from '../../services/api/inspectionsService';

const INSPECTION_TYPES: InspectionType[] = ['quality', 'safety', 'material', 'permit', 'handover', 'workmanship', 'other'];
const INSPECTION_STATUSES: InspectionStatus[] = ['planned', 'scheduled', 'in_progress', 'passed', 'failed', 'cancelled'];
const CHECKLIST_STATUSES: ChecklistItemStatus[] = ['pending', 'pass', 'fail', 'na'];
const NCR_SEVERITIES: NcrSeverity[] = ['low', 'medium', 'high', 'critical'];

const emptyInspection = (projectId: string | null): InspectionPayload => ({
  projectId: projectId ?? '',
  inspectionNumber: `INSP-${Date.now().toString().slice(-6)}`,
  title: '',
  inspectionType: 'quality',
  status: 'planned',
  scheduledAt: '',
  taskId: null,
});

const emptyNcr = (projectId: string | null, inspection?: InspectionRow | null): NcrPayload => ({
  projectId: projectId ?? '',
  inspectionId: inspection?.id ?? null,
  taskId: inspection?.taskId ?? null,
  ncrNumber: `NCR-${Date.now().toString().slice(-6)}`,
  title: '',
  description: '',
  severity: 'medium',
  status: 'open',
  dueDate: '',
});

const dateInput = (value?: string | null) => value ? value.slice(0, 10) : '';
const dateTimeInput = (value?: string | null) => value ? value.slice(0, 16) : '';
const labelize = (value: string) => value.replace(/_/g, ' ');

// Outline applied to a summary card when it is the active filter.
const filterOutline = (active: boolean) => ({
  borderRadius: 1,
  outline: active ? '2px solid' : '2px solid transparent',
  outlineColor: active ? 'primary.main' : 'transparent',
  transition: 'outline-color 0.2s',
});

const inspectionStatusColor = (status: InspectionStatus): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (status === 'passed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'in_progress' || status === 'scheduled') return 'warning';
  if (status === 'cancelled') return 'default';
  return 'info';
};

const ncrStatusColor = (status: NcrStatus): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if (status === 'closed' || status === 'void') return status === 'closed' ? 'success' : 'default';
  if (status === 'verification') return 'info';
  if (status === 'corrective_action' || status === 'containment') return 'warning';
  return 'error';
};

const severityColor = (severity: NcrSeverity): 'success' | 'warning' | 'error' | 'default' => {
  if (severity === 'low') return 'success';
  if (severity === 'medium') return 'warning';
  if (severity === 'high' || severity === 'critical') return 'error';
  return 'default';
};

const ncrStatusOptions = (currentStatus?: NcrStatus | null): NcrStatus[] => {
  if (!currentStatus) return ['open'];
  const nextByStatus: Partial<Record<NcrStatus, NcrStatus>> = {
    open: 'containment',
    containment: 'corrective_action',
    corrective_action: 'verification',
    verification: 'closed',
  };
  const next = nextByStatus[currentStatus];
  return next ? [currentStatus, next, 'void'] : [currentStatus];
};

export default function InspectionsPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inspections, setInspections] = useState<InspectionRow[]>([]);
  const [ncrs, setNcrs] = useState<NcrRow[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState<InspectionRow | null>(null);
  const [inspectionForm, setInspectionForm] = useState<InspectionPayload>(emptyInspection(null));
  const [checklistInspection, setChecklistInspection] = useState<InspectionRow | null>(null);
  const [itemCode, setItemCode] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemStatus, setItemStatus] = useState<ChecklistItemStatus>('pending');
  const [ncrFormOpen, setNcrFormOpen] = useState(false);
  const [editingNcr, setEditingNcr] = useState<NcrRow | null>(null);
  const [ncrForm, setNcrForm] = useState<NcrPayload>(emptyNcr(null));
  const [activeFilter, setActiveFilter] = useState<'planned' | 'inProgress' | 'failed' | 'openNcrs' | null>(null);

  const toggleFilter = (key: 'planned' | 'inProgress' | 'failed' | 'openNcrs') =>
    setActiveFilter((current) => (current === key ? null : key));

  const fetchData = async () => {
    if (!activeProjectId) return;
    try {
      setLoading(true);
      setError(null);
      const [inspectionRows, ncrRows] = await Promise.all([
        inspectionsService.list({ projectId: activeProjectId }),
        inspectionsService.listNcrs({ projectId: activeProjectId }),
      ]);
      setInspections(inspectionRows);
      setNcrs(ncrRows);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load inspections');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const summary = useMemo(() => {
    const planned = inspections.filter((inspection) => inspection.status === 'planned' || inspection.status === 'scheduled').length;
    const inProgress = inspections.filter((inspection) => inspection.status === 'in_progress').length;
    const failed = inspections.filter((inspection) => inspection.status === 'failed').length;
    const openNcrs = ncrs.filter((ncr) => !['closed', 'void'].includes(ncr.status)).length;
    return { total: inspections.length, planned, inProgress, failed, openNcrs };
  }, [inspections, ncrs]);

  // The three inspection-status cards filter the Inspection Register; the
  // "Open NCRs" card filters the Non-Conformance Reports table instead.
  const filteredInspections = useMemo(() => {
    if (activeFilter === 'planned') {
      return inspections.filter((i) => i.status === 'planned' || i.status === 'scheduled');
    }
    if (activeFilter === 'inProgress') return inspections.filter((i) => i.status === 'in_progress');
    if (activeFilter === 'failed') return inspections.filter((i) => i.status === 'failed');
    return inspections;
  }, [inspections, activeFilter]);

  const filteredNcrs = useMemo(() => {
    if (activeFilter === 'openNcrs') return ncrs.filter((n) => !['closed', 'void'].includes(n.status));
    return ncrs;
  }, [ncrs, activeFilter]);

  const filterLabels: Record<'planned' | 'inProgress' | 'failed' | 'openNcrs', string> = {
    planned: 'Planned',
    inProgress: 'In Progress',
    failed: 'Failed',
    openNcrs: 'Open NCRs',
  };

  const openCreate = () => {
    setEditingInspection(null);
    setInspectionForm(emptyInspection(activeProjectId));
    setFormOpen(true);
  };

  const openEdit = (inspection: InspectionRow) => {
    setEditingInspection(inspection);
    setInspectionForm({
      projectId: inspection.projectId,
      taskId: inspection.taskId,
      inspectionNumber: inspection.inspectionNumber,
      title: inspection.title,
      inspectionType: inspection.inspectionType,
      status: inspection.status,
      scheduledAt: dateTimeInput(inspection.scheduledAt),
      inspectorUserId: inspection.inspectorUserId,
      teamId: inspection.teamId,
      resultSummary: inspection.resultSummary,
      score: inspection.score,
      documentId: inspection.documentId,
    });
    setFormOpen(true);
  };

  const saveInspection = async () => {
    try {
      if (!inspectionForm.projectId) {
        setError('Select a project before creating an inspection.');
        return;
      }
      const payload = {
        ...inspectionForm,
        taskId: inspectionForm.taskId || null,
        scheduledAt: inspectionForm.scheduledAt || null,
        inspectorUserId: inspectionForm.inspectorUserId || null,
        teamId: inspectionForm.teamId || null,
        resultSummary: inspectionForm.resultSummary || null,
        score: inspectionForm.score === undefined || inspectionForm.score === null ? null : Number(inspectionForm.score),
        documentId: inspectionForm.documentId || null,
      };
      if (editingInspection) {
        await inspectionsService.update(editingInspection.id, payload);
      } else {
        await inspectionsService.create(payload);
      }
      setFormOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save inspection');
    }
  };

  const completeInspection = async (inspection: InspectionRow, status: 'passed' | 'failed' | 'cancelled') => {
    try {
      await inspectionsService.complete(inspection.id, {
        status,
        resultSummary: inspection.resultSummary,
        score: inspection.score,
      });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete inspection');
    }
  };

  const addChecklistItem = async () => {
    if (!checklistInspection || !itemCode.trim() || !itemDescription.trim()) return;
    try {
      await inspectionsService.addChecklistItem(checklistInspection.id, {
        itemCode,
        description: itemDescription,
        status: itemStatus,
        sortOrder: (checklistInspection.checklistItems ?? []).length + 1,
      });
      setItemCode('');
      setItemDescription('');
      setItemStatus('pending');
      const refreshed = await inspectionsService.list({ projectId: activeProjectId ?? undefined });
      setInspections(refreshed);
      setChecklistInspection(refreshed.find((inspection) => inspection.id === checklistInspection.id) ?? checklistInspection);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save checklist item');
    }
  };

  const updateChecklistItem = async (itemId: string, status: ChecklistItemStatus) => {
    try {
      await inspectionsService.updateChecklistItem(itemId, { status });
      await fetchData();
      if (checklistInspection) {
        const refreshed = await inspectionsService.list({ projectId: activeProjectId ?? undefined });
        setInspections(refreshed);
        setChecklistInspection(refreshed.find((inspection) => inspection.id === checklistInspection.id) ?? checklistInspection);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update checklist item');
    }
  };

  const deleteChecklistItem = async (itemId: string) => {
    try {
      await inspectionsService.deleteChecklistItem(itemId);
      await fetchData();
      if (checklistInspection) {
        setChecklistInspection({
          ...checklistInspection,
          checklistItems: (checklistInspection.checklistItems ?? []).filter((item) => item.id !== itemId),
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete checklist item');
    }
  };

  const openNcrCreate = (inspection?: InspectionRow) => {
    setEditingNcr(null);
    setNcrForm(emptyNcr(activeProjectId, inspection));
    setNcrFormOpen(true);
  };

  const openNcrEdit = (ncr: NcrRow) => {
    setEditingNcr(ncr);
    setNcrForm({
      projectId: ncr.projectId,
      inspectionId: ncr.inspectionId,
      taskId: ncr.taskId,
      defectId: ncr.defectId,
      ncrNumber: ncr.ncrNumber,
      title: ncr.title,
      description: ncr.description,
      severity: ncr.severity,
      status: ncr.status,
      rootCause: ncr.rootCause,
      correctiveAction: ncr.correctiveAction,
      ownerUserId: ncr.ownerUserId,
      dueDate: dateInput(ncr.dueDate),
      documentId: ncr.documentId,
    });
    setNcrFormOpen(true);
  };

  const saveNcr = async () => {
    try {
      if (!ncrForm.projectId) {
        setError('Select a project before creating an NCR.');
        return;
      }
      const payload = {
        ...ncrForm,
        inspectionId: ncrForm.inspectionId || null,
        taskId: ncrForm.taskId || null,
        defectId: ncrForm.defectId || null,
        description: ncrForm.description || null,
        rootCause: ncrForm.rootCause || null,
        correctiveAction: ncrForm.correctiveAction || null,
        ownerUserId: ncrForm.ownerUserId || null,
        dueDate: ncrForm.dueDate || null,
        documentId: ncrForm.documentId || null,
      };
      if (editingNcr) {
        await inspectionsService.updateNcr(editingNcr.id, payload);
      } else {
        await inspectionsService.createNcr(payload);
      }
      setNcrFormOpen(false);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save NCR');
    }
  };

  const closeNcr = async (ncr: NcrRow) => {
    try {
      await inspectionsService.closeNcr(ncr.id);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to close NCR');
    }
  };

  const inspectionColumns: Column<InspectionRow>[] = [
    { id: 'inspectionNumber', label: 'Inspection No.', minWidth: 130 },
    { id: 'title', label: 'Title', minWidth: 220 },
    {
      id: 'inspectionType',
      label: 'Type',
      minWidth: 120,
      format: (value: InspectionType) => <Chip size="small" label={labelize(value)} />,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      format: (value: InspectionStatus) => <Chip size="small" color={inspectionStatusColor(value)} label={labelize(value)} />,
    },
    {
      id: 'scheduledAt',
      label: 'Scheduled',
      minWidth: 150,
      format: (value: string | null) => value ? value.slice(0, 16).replace('T', ' ') : 'Not scheduled',
    },
    {
      id: 'score',
      label: 'Score',
      minWidth: 90,
      align: 'center',
      format: (value: number | null) => value === null || value === undefined ? '-' : `${value}%`,
    },
    {
      id: 'checklistItems',
      label: 'Checklist',
      minWidth: 110,
      align: 'center',
      format: (_value, row) => {
        const items = row.checklistItems ?? [];
        const failed = items.filter((item) => item.status === 'fail').length;
        return failed > 0 ? `${failed}/${items.length} failed` : `${items.length} items`;
      },
    },
    {
      id: 'ncrs',
      label: 'NCRs',
      minWidth: 80,
      align: 'center',
      format: (_value, row) => (row.ncrs ?? []).filter((ncr) => !['closed', 'void'].includes(ncr.status)).length,
    },
    {
      id: 'actions',
      label: '',
      minWidth: 220,
      sortable: false,
      format: (_value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit inspection">
            <IconButton size="small" onClick={() => openEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Checklist">
            <IconButton size="small" onClick={() => setChecklistInspection(row)}>
              <PlaylistAddCheck fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Create NCR">
            <IconButton size="small" onClick={() => openNcrCreate(row)}>
              <ReportProblem fontSize="small" />
            </IconButton>
          </Tooltip>
          {!['passed', 'failed', 'cancelled'].includes(row.status) && (
            <>
              <Tooltip title="Pass inspection">
                <IconButton size="small" onClick={() => completeInspection(row, 'passed')}>
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Fail inspection">
                <IconButton size="small" onClick={() => completeInspection(row, 'failed')}>
                  <AssignmentLate fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  const ncrColumns: Column<NcrRow>[] = [
    { id: 'ncrNumber', label: 'NCR No.', minWidth: 120 },
    { id: 'title', label: 'Title', minWidth: 220 },
    {
      id: 'severity',
      label: 'Severity',
      minWidth: 110,
      format: (value: NcrSeverity) => <Chip size="small" color={severityColor(value)} label={value} />,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 150,
      format: (value: NcrStatus) => <Chip size="small" color={ncrStatusColor(value)} label={labelize(value)} />,
    },
    {
      id: 'dueDate',
      label: 'Due',
      minWidth: 120,
      format: (value: string | null) => value ? value.slice(0, 10) : 'No due date',
    },
    {
      id: 'inspectionId',
      label: 'Inspection',
      minWidth: 130,
      format: (value: string | null) => {
        const inspection = inspections.find((row) => row.id === value);
        return inspection?.inspectionNumber ?? '-';
      },
    },
    {
      id: 'actions',
      label: '',
      minWidth: 110,
      sortable: false,
      format: (_value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit NCR">
            <IconButton size="small" onClick={() => openNcrEdit(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {row.status === 'verification' && (
            <Tooltip title="Close NCR">
              <IconButton size="small" onClick={() => closeNcr(row)}>
                <CheckCircle fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  if (!activeProjectId) {
    return (
      <Box>
        <PageHeader title="Inspections & NCRs" />
        <Alert severity="info">Select a project to view inspections and NCRs.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Inspections & NCRs"
        subtitle={`${summary.total} inspections tracked`}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button startIcon={<ReportProblem />} variant="outlined" onClick={() => openNcrCreate()}>
              New NCR
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={openCreate}>
              New Inspection
            </Button>
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: activeFilter ? 1.5 : 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={filterOutline(activeFilter === 'planned')}>
            <KPICard title="Planned" value={`${summary.planned}`} icon={<FactCheck />} color="info" onClick={() => toggleFilter('planned')} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={filterOutline(activeFilter === 'inProgress')}>
            <KPICard title="In Progress" value={`${summary.inProgress}`} icon={<Refresh />} color="warning" onClick={() => toggleFilter('inProgress')} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={filterOutline(activeFilter === 'failed')}>
            <KPICard title="Failed" value={`${summary.failed}`} icon={<AssignmentLate />} color={summary.failed > 0 ? 'error' : 'success'} onClick={() => toggleFilter('failed')} />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={filterOutline(activeFilter === 'openNcrs')}>
            <KPICard title="Open NCRs" value={`${summary.openNcrs}`} icon={<ReportProblem />} color={summary.openNcrs > 0 ? 'error' : 'success'} onClick={() => toggleFilter('openNcrs')} />
          </Box>
        </Grid>
      </Grid>
      {activeFilter && (
        <Box sx={{ mb: 3 }}>
          <Chip
            color="primary"
            variant="outlined"
            label={`Filtering: ${filterLabels[activeFilter]}`}
            onDelete={() => setActiveFilter(null)}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Inspection Register
              {(activeFilter === 'planned' || activeFilter === 'inProgress' || activeFilter === 'failed') && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  · {filteredInspections.length} of {inspections.length}
                </Typography>
              )}
            </Typography>
            <DataTable columns={inspectionColumns} data={filteredInspections} searchable pagination defaultRowsPerPage={10} />
          </Box>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Non-Conformance Reports
              {activeFilter === 'openNcrs' && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  · {filteredNcrs.length} of {ncrs.length}
                </Typography>
              )}
            </Typography>
            <DataTable columns={ncrColumns} data={filteredNcrs} searchable pagination defaultRowsPerPage={10} />
          </Box>
        </Box>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingInspection ? 'Edit Inspection' : 'New Inspection'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pt: 2 }}>
          <TextField label="Inspection No." value={inspectionForm.inspectionNumber} onChange={(e) => setInspectionForm({ ...inspectionForm, inspectionNumber: e.target.value })} />
          <TextField label="Title" value={inspectionForm.title} onChange={(e) => setInspectionForm({ ...inspectionForm, title: e.target.value })} />
          <TextField select label="Type" value={inspectionForm.inspectionType} onChange={(e) => setInspectionForm({ ...inspectionForm, inspectionType: e.target.value as InspectionType })}>
            {INSPECTION_TYPES.map((type) => <MenuItem key={type} value={type}>{labelize(type)}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={inspectionForm.status ?? 'planned'} onChange={(e) => setInspectionForm({ ...inspectionForm, status: e.target.value as InspectionStatus })}>
            {INSPECTION_STATUSES.map((status) => <MenuItem key={status} value={status}>{labelize(status)}</MenuItem>)}
          </TextField>
          <TextField type="datetime-local" label="Scheduled" InputLabelProps={{ shrink: true }} value={dateTimeInput(inspectionForm.scheduledAt)} onChange={(e) => setInspectionForm({ ...inspectionForm, scheduledAt: e.target.value })} />
          <TextField label="Task ID" value={inspectionForm.taskId ?? ''} onChange={(e) => setInspectionForm({ ...inspectionForm, taskId: e.target.value || null })} />
          <TextField label="Inspector User ID" value={inspectionForm.inspectorUserId ?? ''} onChange={(e) => setInspectionForm({ ...inspectionForm, inspectorUserId: e.target.value || null })} />
          <TextField label="Team ID" value={inspectionForm.teamId ?? ''} onChange={(e) => setInspectionForm({ ...inspectionForm, teamId: e.target.value || null })} />
          <TextField type="number" label="Score" value={inspectionForm.score ?? ''} onChange={(e) => setInspectionForm({ ...inspectionForm, score: e.target.value === '' ? null : Number(e.target.value) })} />
          <TextField label="Result Summary" value={inspectionForm.resultSummary ?? ''} onChange={(e) => setInspectionForm({ ...inspectionForm, resultSummary: e.target.value })} multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveInspection}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(checklistInspection)} onClose={() => setChecklistInspection(null)} maxWidth="md" fullWidth>
        <DialogTitle>Inspection Checklist</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {checklistInspection && (
            <>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{checklistInspection.inspectionNumber} - {checklistInspection.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '130px 1fr 150px auto' }, gap: 1.5, mb: 2 }}>
                <TextField label="Code" value={itemCode} onChange={(e) => setItemCode(e.target.value)} />
                <TextField label="Checklist Item" value={itemDescription} onChange={(e) => setItemDescription(e.target.value)} />
                <TextField select label="Status" value={itemStatus} onChange={(e) => setItemStatus(e.target.value as ChecklistItemStatus)}>
                  {CHECKLIST_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={addChecklistItem} startIcon={<Add />}>Add</Button>
              </Box>
              <Box sx={{ display: 'grid', gap: 1 }}>
                {(checklistInspection.checklistItems ?? []).map((item) => (
                  <Box key={item.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '120px 1fr 150px 44px' }, gap: 1, alignItems: 'center', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold">{item.itemCode}</Typography>
                    <Typography variant="body2">{item.description}</Typography>
                    <TextField select size="small" value={item.status} onChange={(e) => updateChecklistItem(item.id, e.target.value as ChecklistItemStatus)}>
                      {CHECKLIST_STATUSES.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                    </TextField>
                    <Tooltip title="Delete item">
                      <IconButton size="small" onClick={() => deleteChecklistItem(item.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                {(checklistInspection.checklistItems ?? []).length === 0 && <Alert severity="info">No checklist items recorded.</Alert>}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChecklistInspection(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ncrFormOpen} onClose={() => setNcrFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingNcr ? 'Edit NCR' : 'New NCR'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, pt: 2 }}>
          <TextField label="NCR No." value={ncrForm.ncrNumber} onChange={(e) => setNcrForm({ ...ncrForm, ncrNumber: e.target.value })} />
          <TextField label="Title" value={ncrForm.title} onChange={(e) => setNcrForm({ ...ncrForm, title: e.target.value })} />
          <TextField select label="Severity" value={ncrForm.severity ?? 'medium'} onChange={(e) => setNcrForm({ ...ncrForm, severity: e.target.value as NcrSeverity })}>
            {NCR_SEVERITIES.map((severity) => <MenuItem key={severity} value={severity}>{severity}</MenuItem>)}
          </TextField>
          <TextField select label="Status" value={ncrForm.status ?? 'open'} onChange={(e) => setNcrForm({ ...ncrForm, status: e.target.value as NcrStatus })}>
            {ncrStatusOptions(editingNcr?.status).map((status) => <MenuItem key={status} value={status}>{labelize(status)}</MenuItem>)}
          </TextField>
          <TextField select label="Inspection" value={ncrForm.inspectionId ?? ''} onChange={(e) => {
            const inspection = inspections.find((row) => row.id === e.target.value);
            setNcrForm({ ...ncrForm, inspectionId: e.target.value || null, taskId: inspection?.taskId ?? ncrForm.taskId ?? null });
          }}>
            <MenuItem value="">None</MenuItem>
            {inspections.map((inspection) => (
              <MenuItem key={inspection.id} value={inspection.id}>{inspection.inspectionNumber} - {inspection.title}</MenuItem>
            ))}
          </TextField>
          <TextField type="date" label="Due Date" InputLabelProps={{ shrink: true }} value={dateInput(ncrForm.dueDate)} onChange={(e) => setNcrForm({ ...ncrForm, dueDate: e.target.value })} />
          <TextField label="Task ID" value={ncrForm.taskId ?? ''} onChange={(e) => setNcrForm({ ...ncrForm, taskId: e.target.value || null })} />
          <TextField label="Defect ID" value={ncrForm.defectId ?? ''} onChange={(e) => setNcrForm({ ...ncrForm, defectId: e.target.value || null })} />
          <TextField label="Description" value={ncrForm.description ?? ''} onChange={(e) => setNcrForm({ ...ncrForm, description: e.target.value })} multiline minRows={2} />
          <TextField label="Corrective Action" value={ncrForm.correctiveAction ?? ''} onChange={(e) => setNcrForm({ ...ncrForm, correctiveAction: e.target.value })} multiline minRows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNcrFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveNcr}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
